import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios'; // Axios 설치 필요
import PageConfig from '@/component/layout/PageConfig.tsx';
import Input from '@/component/input/Input.tsx';
import { apiSignup, IReqSignup, apiCheckEmail } from '@/api/auth/signup.ts';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { isEmpty } from '@/util/strings.ts';
import DaumPostcode from 'react-daum-postcode';
import { GOOGLE_MAPS_API_KEY } from '@/constant/constant.ts';
import PrivacyPolicy from '@/component/privacy/PrivacyPolicy.tsx';

// 이메일 형식 검증을 위한 정규식
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const Signup: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [currentStep, setCurrentStep] = useState(1); // 1: 개인정보 동의, 2: 기본정보, 3: 주소정보, 4: 보호자정보
    const [privacyAgreed, setPrivacyAgreed] = useState(false);
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
    const [formData, setFormData] = useState<IReqSignup>({
        email: '',
        password: '',
        name: '',
        contact: '',
        address: '',
        latitude: '',
        longitude: '',
        centerName: '',
        centerContact: '',
        gender: 0,
        birthDate: '',
        inspectorMemo: '',
    });

    const [isPostcodeOpen, setIsPostcodeOpen] = useState(false);
    const [basicAddress, setBasicAddress] = useState('');
    const [detailedAddress, setDetailedAddress] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [passwordError, setPasswordError] = useState(false);
    const [passwordLengthError, setPasswordLengthError] = useState(false);
    const [isEmailChecking, setIsEmailChecking] = useState(false);
    const [isEmailAvailable, setIsEmailAvailable] = useState<boolean | null>(null);
    const [isEmailFormatValid, setIsEmailFormatValid] = useState<boolean | null>(null);
    
    // 디바운스 타이머를 위한 ref
    const emailCheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // 이메일 형식 검증 함수
    const validateEmailFormat = (email: string): boolean => {
        return EMAIL_REGEX.test(email);
    };

    // 입력 필드 변경 핸들러
    const onInputChange = (key: keyof IReqSignup, value: string | number) => {
        if (key === 'password' && typeof value === 'string') {
            setPasswordLengthError(value.length > 0 && value.length < 8);
            setFormData((prev) => ({ ...prev, [key]: value }));
        } else if (key === 'email') {
            // email은 항상 string으로 처리
            const emailValue = String(value);
            setFormData((prev) => ({ ...prev, [key]: emailValue }));
            
            // 이메일 변경 시 상태 초기화
            if (emailValue !== formData.email) {
                setIsEmailAvailable(null);
                
                // 기존 타이머 취소
                if (emailCheckTimerRef.current) {
                    clearTimeout(emailCheckTimerRef.current);
                }
                
                // 이메일 형식 검증
                if (!isEmpty(emailValue)) {
                    const isValid = validateEmailFormat(emailValue);
                    setIsEmailFormatValid(isValid);
                    
                    // 이메일 형식이 유효할 때만 중복 확인 수행
                    if (isValid) {
                        setIsEmailChecking(true);
                        emailCheckTimerRef.current = setTimeout(() => {
                            checkEmailAvailability(emailValue);
                        }, 800); // 0.8초 지연
                    } else {
                        setIsEmailChecking(false);
                    }
                } else {
                    setIsEmailFormatValid(null);
                    setIsEmailChecking(false);
                }
            }
        } else {
            setFormData((prev) => ({ ...prev, [key]: value }));
        }
    };

    const validatePassword = (confirmPassword: string) => {
        setPasswordConfirm(confirmPassword);
        setPasswordError(formData.password !== confirmPassword);
    };

    // Google Maps Geocoding API를 호출하여 위도/경도 가져오기
    const fetchCoordinates = async (address: string) => {
        const API_KEY = GOOGLE_MAPS_API_KEY; // Google Maps API Key를 여기에 추가
        const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${API_KEY}`;
        try {
            const response = await axios.get(url);
            const location = response.data.results[0]?.geometry?.location;
            if (location) {
                setFormData((prev) => ({
                    ...prev,
                    latitude: location.lat.toString(),
                    longitude: location.lng.toString(),
                }));
            }
        } catch (error) {
            console.error('Geocoding API 호출 실패:', error);
            toast.error(t('signup.coord_error'));
        }
    };

    // 우편번호 검색 결과 처리
    const handleComplete = (data: any) => {
        let fullAddress = data.roadAddress || data.jibunAddress;
        let extraAddress = '';

        if (data.bname && /[동|로|가]$/g.test(data.bname)) {
            extraAddress += data.bname;
        }
        if (data.buildingName && data.apartment === 'Y') {
            extraAddress += (extraAddress ? `, ${data.buildingName}` : data.buildingName);
        }

        if (extraAddress) {
            fullAddress += ` (${extraAddress})`;
        }

        setBasicAddress(fullAddress);
        setDetailedAddress('');
        setIsPostcodeOpen(false);
        fetchCoordinates(fullAddress);
    };

    // 상세주소 입력 핸들러
    const handleDetailedAddressChange = (value: string) => {
        setDetailedAddress(value);
        setFormData((prev) => ({
            ...prev,
            address: `${basicAddress} ${value}`.trim(),
        }));
    };

    // 이메일 중복 확인
    const checkEmailAvailability = async (email?: string) => {
        const emailToCheck = email || formData.email;
        
        if (isEmpty(emailToCheck)) {
            setIsEmailChecking(false);
            return;
        }

        // 형식 검증 실패 시 API 호출하지 않음
        if (!validateEmailFormat(emailToCheck)) {
            setIsEmailChecking(false);
            setIsEmailFormatValid(false);
            return;
        }

        try {
            setIsEmailChecking(true);
            const response = await apiCheckEmail(emailToCheck);
            
            if (response.code === 200) {
                // 타입 안전하게 확인
                const exists = response.data && typeof response.data === 'object' && 'exists' in response.data 
                    ? Boolean(response.data.exists) 
                    : false;
                
                setIsEmailAvailable(!exists);
                
                if (exists) {
                    // 토스트 알림은 자동 체크에서는 제거 (시각적 피드백만 제공)
                    if (!email) {
                        toast.error(t('signup.email_already_exists'));
                    }
                } else if (!email) { // 수동 확인 시에만 성공 메시지 표시
                    toast.success(t('signup.email_available'));
                }
            } else {
                if (!email) { // 수동 확인 시에만 오류 메시지 표시
                    toast.error(t('signup.email_check_error'));
                }
                setIsEmailAvailable(null);
            }
        } catch (error) {
            console.error('이메일 중복 확인 오류:', error);
            if (!email) { // 수동 확인 시에만 오류 메시지 표시
                toast.error(t('signup.email_check_error'));
            }
            setIsEmailAvailable(null);
        } finally {
            setIsEmailChecking(false);
        }
    };

    // 컴포넌트 언마운트 시 타이머 정리
    useEffect(() => {
        return () => {
            if (emailCheckTimerRef.current) {
                clearTimeout(emailCheckTimerRef.current);
            }
        };
    }, []);

    const validateStep = (step: number): boolean => {
        if (step === 1) {
            if (!privacyAgreed) {
                toast.error(t('signup.privacy_agreement_required'));
                return false;
            }
        } else if (step === 2) {
            if (isEmpty(formData.email) || isEmpty(formData.password) || 
                isEmpty(formData.name) || isEmpty(formData.birthDate) || 
                isEmpty(formData.contact) || passwordError || passwordLengthError) {
                if (passwordLengthError) {
                    toast.error(t('signup.password_min_length_error'));
                } else {
                    toast.error(t('signup.required_fields'));
                }
                return false;
            }
            
            // 이메일 형식 검증 실패 시
            if (isEmailFormatValid === false) {
                toast.error(t('signup.invalid_email_format'));
                return false;
            }
            
            // 이메일 중복 확인이 되지 않았거나 이미 사용 중인 이메일인 경우
            if (isEmailAvailable !== true) {
                toast.error(t('signup.email_check_required'));
                return false;
            }
        } else if (step === 3) {
            if (isEmpty(formData.address)) {
                toast.error(t('signup.address_required'));
                return false;
            }
        } else if (step === 4) {
            if (isEmpty(formData.centerName) || isEmpty(formData.centerContact)) {
                toast.error(t('signup.guardian_info_required'));
                return false;
            }
        }
        return true;
    };

    const nextStep = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(prev => prev + 1);
        }
    };

    const prevStep = () => {
        setCurrentStep(prev => prev - 1);
    };

    const onSubmit = async () => {
        if (!validateStep(currentStep)) return;

        try {
            const response = await apiSignup({
                ...formData,
                privacyAgreed: privacyAgreed
            });
            if (response) {
                toast.success(t('signup.signup_complete'));
                navigate('/login');
            }
        } catch (error) {
            toast.error(t('signup.signup_error'));
        }
    };

    // 이메일 유효성 및 필수 입력값 검증
    const isEmailValid = isEmailFormatValid === true && isEmailAvailable === true;
    
    // 각 단계별 필수 입력값 검증
    const isStep1Valid = privacyAgreed;
    
    const isStep2Valid = !isEmpty(formData.email) && !isEmpty(formData.password) && 
                       !isEmpty(formData.name) && !isEmpty(formData.birthDate) && 
                       !isEmpty(formData.contact) && !passwordError && !passwordLengthError && 
                       isEmailValid;
    
    // 주소 정보 단계 유효성 검증
    const isStep3Valid = !isEmpty(formData.address);
    
    // 보호자 정보 단계 유효성 검증
    const isStep4Valid = !isEmpty(formData.centerName) && !isEmpty(formData.centerContact);
    
    // 현재 단계에 따른 버튼 비활성화 상태 확인
    const isNextButtonDisabled = 
        (currentStep === 1 && !isStep1Valid) ||
        (currentStep === 2 && !isStep2Valid) ||
        (currentStep === 3 && !isStep3Valid);
        
    // 가입 완료 버튼 비활성화 상태 확인
    const isSubmitButtonDisabled = currentStep === 4 && !isStep4Valid;

    // 스크롤 이벤트 핸들러
    const handlePrivacyScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const target = e.currentTarget;
        const scrollTop = target.scrollTop;
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;
        
        // 스크롤이 끝까지 내려갔는지 확인 (약간의 여유를 둠)
        if (scrollTop + clientHeight >= scrollHeight - 10) {
            setHasScrolledToBottom(true);
        }
    };

    // 단계 진행 표시
    const ProgressBar = () => {
        return (
            <div className="w-full mb-8">
                <div className="flex justify-between items-center mb-2">
                    <div className="text-xs font-medium whitespace-nowrap">{t('signup.privacy_agreement')}</div>
                    <div className="text-xs font-medium whitespace-nowrap">{t('signup.basic_info')}</div>
                    <div className="text-xs font-medium whitespace-nowrap">{t('signup.address_info')}</div>
                    <div className="text-xs font-medium whitespace-nowrap">{t('signup.guardian_info')}</div>
                </div>
                <div className="relative h-2 bg-gray-200 rounded-full">
                    <div 
                        className="absolute h-2 bg-gradient-to-r from-blue-500 to-teal-400 rounded-full transition-all duration-300"
                        style={{ width: `${(currentStep / 4) * 100}%` }}
                    ></div>
                </div>
                <div className="flex justify-between items-center mt-1">
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs ${currentStep >= 1 ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>1</div>
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs ${currentStep >= 2 ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>2</div>
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs ${currentStep >= 3 ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>3</div>
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs ${currentStep >= 4 ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>4</div>
                </div>
            </div>
        );
    };

    return (
        <PageConfig showFooter={false}>
            <div className="bg-gradient-to-b from-blue-50 to-white min-h-screen w-full py-6 px-4 overflow-y-auto">
                <div className="w-full max-w-[500px] bg-white p-6 rounded-lg shadow-xl mx-auto mb-12">
                    <ProgressBar />
                    
                    <h1 className="text-3xl font-bold mb-6 text-center bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-teal-500 font-sans">
                        {t('signup.title')}
                    </h1>
                    
                    {currentStep === 1 && (
                        <div className="space-y-5 animate-fadeIn">
                            <h2 className="text-xl font-semibold mb-4 text-gray-700 font-sans">{t('signup.privacy_agreement')}</h2>
                            
                            <div 
                                className="border border-gray-300 rounded-lg p-4 max-h-[400px] overflow-y-auto bg-gray-50 relative"
                                onScroll={handlePrivacyScroll}
                            >
                                <PrivacyPolicy />
                                {!hasScrolledToBottom && (
                                    <div className="sticky bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-50 via-gray-50/80 to-transparent pointer-events-none flex items-end justify-center pb-2">
                                        <div className="text-xs text-gray-600 font-semibold animate-bounce">
                                            ↓ 아래로 스크롤하여 전체 내용을 확인해주세요 ↓
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            <div className={`flex items-start gap-3 p-4 rounded-lg border transition-all ${
                                hasScrolledToBottom 
                                    ? 'bg-blue-50 border-blue-200' 
                                    : 'bg-gray-100 border-gray-300'
                            }`}>
                                <input
                                    type="checkbox"
                                    id="privacyAgree"
                                    checked={privacyAgreed}
                                    onChange={(e) => setPrivacyAgreed(e.target.checked)}
                                    disabled={!hasScrolledToBottom}
                                    className={`mt-1 w-5 h-5 accent-blue-500 flex-shrink-0 ${
                                        hasScrolledToBottom ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                                    }`}
                                />
                                <label 
                                    htmlFor="privacyAgree" 
                                    className={`text-sm font-sans select-none ${
                                        hasScrolledToBottom 
                                            ? 'text-gray-700 cursor-pointer' 
                                            : 'text-gray-400 cursor-not-allowed'
                                    }`}
                                >
                                    <span className={`font-semibold ${
                                        hasScrolledToBottom ? 'text-blue-700' : 'text-gray-400'
                                    }`}>(필수)</span> 개인정보 및 민감정보 처리방침을 읽었으며, 이에 동의합니다.
                                </label>
                            </div>
                            
                            {!hasScrolledToBottom && (
                                <div className="text-sm text-orange-600 font-sans flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    개인정보 처리방침을 끝까지 읽어야 동의할 수 있습니다.
                                </div>
                            )}
                            
                            {hasScrolledToBottom && !privacyAgreed && (
                                <div className="text-sm text-red-600 font-sans">
                                    회원가입을 진행하시려면 개인정보 처리방침에 동의해주세요.
                                </div>
                            )}
                        </div>
                    )}
                    
                    {currentStep === 2 && (
                        <div className="space-y-5 animate-fadeIn">
                            <h2 className="text-xl font-semibold mb-4 text-gray-700 font-sans">{t('signup.basic_info')}</h2>
                            <div className="relative">
                                <div className="relative mb-1">
                                    <Input
                                        className={`w-full ${isEmailFormatValid === false ? 'border-red-500' : ''}`}
                                        inputType="text"
                                        placeholder={t('signup.email_placeholder')}
                                        value={formData.email}
                                        onChanged={(text) => onInputChange('email', text)}
                                        autoComplete="off"
                                    />
                                    {isEmailChecking && (
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                        </div>
                                    )}
                                </div>
                                <div className="text-xs mb-3 whitespace-normal font-sans">
                                    {isEmailFormatValid === false && (
                                        <span className="text-red-600">{t('signup.invalid_email_format')}</span>
                                    )}
                                    {isEmailFormatValid === true && isEmailAvailable === true && (
                                        <span className="text-green-600">{t('signup.email_available')}</span>
                                    )}
                                    {isEmailFormatValid === true && isEmailAvailable === false && (
                                        <span className="text-red-600">{t('signup.email_already_exists')}</span>
                                    )}
                                    {(isEmailFormatValid === null || (isEmailFormatValid === true && isEmailAvailable === null)) && (
                                        <span className="text-gray-500">{t('signup.email_desc')}</span>
                                    )}
                                </div>
                            </div>
                            
                            <Input
                                className="mb-3 w-full"
                                inputType="password"
                                placeholder={t('signup.password')}
                                value={formData.password}
                                onChanged={(text) => onInputChange('password', text)}
                                autoComplete="new-password"
                                error={passwordLengthError ? t('signup.password_min_length_error') : undefined}
                            />
                            
                            <div className="relative">
                                <Input
                                    className={`mb-1 w-full ${passwordError ? 'border-red-500' : ''}`}
                                    inputType="password"
                                    placeholder={t('signup.password_confirm')}
                                    value={passwordConfirm}
                                    onChanged={validatePassword}
                                    autoComplete="new-password"
                                />
                                {passwordError && (
                                    <div className="text-xs text-red-500 mb-3 whitespace-normal font-sans">{t('signup.password_not_match')}</div>
                                )}
                            </div>
                            
                            <Input
                                className="mb-3 w-full"
                                inputType="text"
                                placeholder={t('signup.name')}
                                value={formData.name}
                                onChanged={(text) => onInputChange('name', text)}
                            />
                            
                            <Input
                                className="mb-3 w-full"
                                inputType="text"
                                placeholder={t('signup.contact')}
                                value={formData.contact}
                                onChanged={(text) => onInputChange('contact', text)}
                            />
                            
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 mb-2 font-sans">{t('signup.gender')}</label>
                                <div className="flex items-center gap-6 bg-gray-50 p-3 rounded-md">
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="radio"
                                            name="gender"
                                            value={0}
                                            checked={formData.gender === 0}
                                            onChange={() => onInputChange('gender', 0)}
                                            className="mr-2 accent-blue-500"
                                        />
                                        <span className="text-gray-700 font-sans">{t('signup.gender.male')}</span>
                                    </label>
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="radio"
                                            name="gender"
                                            value={1}
                                            checked={formData.gender === 1}
                                            onChange={() => onInputChange('gender', 1)}
                                            className="mr-2 accent-blue-500"
                                        />
                                        <span className="text-gray-700 font-sans">{t('signup.gender.female')}</span>
                                    </label>
                                </div>
                            </div>
                            
                            <div className="mb-5">
                                <label className="block text-sm font-medium text-gray-700 mb-2 font-sans">{t('signup.birthdate')}</label>
                                <Input
                                    className="w-full"
                                    inputType="date"
                                    value={formData.birthDate}
                                    onChanged={(text) => onInputChange('birthDate', text)}
                                />
                            </div>
                        </div>
                    )}

                    {currentStep === 3 && (
                        <div className="space-y-5 animate-fadeIn">
                            <h2 className="text-xl font-semibold mb-4 text-gray-700 font-sans">{t('signup.address_info')}</h2>
                            
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 mb-2 font-sans">{t('signup.address')}</label>
                                <div className="flex gap-2 mb-2">
                                    <Input
                                        className="w-full"
                                        inputType="text"
                                        placeholder={t('signup.address_search_placeholder')}
                                        value={basicAddress}
                                        disabled
                                    />
                                    <button
                                        onClick={() => setIsPostcodeOpen(true)}
                                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors text-sm whitespace-nowrap"
                                    >
                                        {t('signup.find_zipcode')}
                                    </button>
                                </div>
                                
                                <Input
                                    className="w-full"
                                    inputType="text"
                                    placeholder={t('signup.detail_address')}
                                    value={detailedAddress}
                                    onChanged={handleDetailedAddressChange}
                                />
                            </div>
                            
                            {isPostcodeOpen && (
                                <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50">
                                    <div className="bg-white p-6 rounded-lg shadow-2xl relative max-w-xl w-full">
                                        <button
                                            className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
                                            onClick={() => setIsPostcodeOpen(false)}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                        <h3 className="text-lg font-medium mb-4 text-center font-sans">{t('signup.address_search')}</h3>
                                        <DaumPostcode onComplete={handleComplete} />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {currentStep === 4 && (
                        <div className="space-y-5 animate-fadeIn">
                            <h2 className="text-xl font-semibold mb-4 text-gray-700 font-sans">{t('signup.guardian_info')}</h2>
                            
                            <Input
                                className="mb-3 w-full"
                                inputType="text"
                                placeholder={t('signup.guardian_name')}
                                value={formData.centerName}
                                onChanged={(text) => onInputChange('centerName', text)}
                            />
                            
                            <Input
                                className="mb-3 w-full"
                                inputType="text"
                                placeholder={t('signup.guardian_contact')}
                                value={formData.centerContact}
                                onChanged={(text) => onInputChange('centerContact', text)}
                            />
                            
                            <div className="mb-5">
                                <label className="block text-sm font-medium text-gray-700 mb-2 font-sans">{t('signup.caution')}</label>
                                <textarea
                                    className="w-full h-32 p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    placeholder={t('signup.caution_placeholder')}
                                    value={formData.inspectorMemo}
                                    onChange={(e) => onInputChange('inspectorMemo', e.target.value)}
                                ></textarea>
                            </div>
                        </div>
                    )}

                    <div className="flex gap-3 mt-8 mb-4">
                        {currentStep > 1 ? (
                            <button
                                onClick={prevStep}
                                className="w-full h-10 rounded-lg font-medium transition-all duration-300 bg-white border-2 border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-300 flex items-center justify-center font-sans"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                                </svg>
                                <span className="whitespace-nowrap text-xs">{t('signup.prev')}</span>
                            </button>
                        ) : (
                            <button
                                onClick={() => navigate(-1)}
                                className="w-full h-10 rounded-lg font-medium transition-all duration-300 bg-white border-2 border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-300 flex items-center justify-center font-sans"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                <span className="whitespace-nowrap text-xs">{t('signup.cancel')}</span>
                            </button>
                        )}
                        
                        {currentStep < 4 ? (
                            <button
                                onClick={nextStep}
                                disabled={isNextButtonDisabled}
                                className={`w-full h-10 rounded-lg font-medium transition-all duration-300 flex items-center justify-center font-sans
                                    ${isNextButtonDisabled
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                        : 'bg-gradient-to-r from-blue-500 to-teal-400 text-white hover:from-blue-600 hover:to-teal-500 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-400'
                                    }`}
                            >
                                <span className="whitespace-nowrap text-xs">{t('signup.next')}</span>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        ) : (
                            <button
                                onClick={onSubmit}
                                disabled={isSubmitButtonDisabled}
                                className={`w-full h-10 rounded-lg font-medium transition-all duration-300 flex items-center justify-center font-sans
                                    ${isSubmitButtonDisabled
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                        : 'bg-gradient-to-r from-blue-500 to-teal-400 text-white hover:from-blue-600 hover:to-teal-500 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-400'
                                    }`}
                            >
                                <span className="whitespace-nowrap text-xs">{t('signup.complete')}</span>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </PageConfig>
    );
};

export default Signup;
